/**
 * EditListModal - Edit list name, icon, and color
 *
 * Uses ListFormFields for consistent form rendering.
 */
import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import type { CardList } from '@/types/lists';
import { UpdateListSchema } from '@/types/schemas';

import { useUpdateList } from '../hooks/api';
import ListFormFields, { type ListFormData } from './ListFormFields';

// ============================================================================
// Types
// ============================================================================

export interface EditListModalProps {
  isOpen: boolean;
  onClose: () => void;
  list: CardList;
  /** Called after successful update */
  onSuccess?: () => void;
}

// ============================================================================
// Component
// ============================================================================

export default function EditListModal({ isOpen, onClose, list, onSuccess }: EditListModalProps): ReactElement {
  const [formData, setFormData] = useState<ListFormData>({
    name: '',
    icon: '📋',
    color: null,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isPending, setIsPending] = useState(false);

  const updateList = useUpdateList();

  // Initialize form with list data when modal opens
  useEffect(() => {
    if (isOpen && list) {
      setFormData({
        name: list.name,
        icon: list.icon || '📋',
        color: list.color || null,
      });
      setErrors({});
      setIsPending(false);
    }
  }, [isOpen, list]);

  const handleChange = useCallback((data: ListFormData) => {
    setFormData(data);
    // Clear any existing errors when user makes changes
    setErrors({});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const updateData = {
      name: formData.name.trim(),
      icon: formData.icon,
      color: formData.color,
    };

    // Validate with Zod
    const result = UpdateListSchema.safeParse(updateData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const path = issue.path[0];
        if (path && typeof path === 'string') {
          fieldErrors[path] = issue.message;
        }
      }
      setErrors(fieldErrors);
      return;
    }

    setIsPending(true);
    try {
      await updateList(list.id, result.data);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to update list:', error);
      setErrors({ submit: 'Failed to update list. Please try again.' });
    } finally {
      setIsPending(false);
    }
  };

  const hasChanges = formData.name !== list.name || formData.icon !== (list.icon || '📋') || formData.color !== (list.color || null);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit List" size="sm">
      <form onSubmit={handleSubmit} className="space-y-5">
        <ListFormFields data={formData} onChange={handleChange} errors={errors} disabled={isPending} showPreview={true} />

        {/* Submit error */}
        {errors.submit && <div className="bg-danger/10 text-danger rounded-lg px-4 py-3 text-sm">{errors.submit}</div>}

        {/* Actions */}
        <div className="border-storm flex justify-end gap-3 border-t pt-4">
          <Button type="button" text="Cancel" variant="text" onClick={onClose} disabled={isPending} />
          <Button
            type="submit"
            text={isPending ? 'Saving...' : 'Save Changes'}
            onClick={() => {}}
            disabled={!formData.name.trim() || !hasChanges || isPending}
          />
        </div>
      </form>
    </Modal>
  );
}
